import { useEffect, useMemo, useState } from "react";
import { fetchProperties } from "../../../store/slices/propertySlice";
import { fetchUnits } from "../../../store/slices/unitSlice";
import {
  cancelBooking,
  createBooking,
  fetchBookings,
  sendBookingInvoice,
  updateBooking,
} from "../../../store/slices/bookingSlice";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useTranslation } from "../../../translations/i18n";
import { BookingResponse } from "../../../models/ResponseModels/BookingResponse";
import { UnitResponse } from "../../../models/ResponseModels/UnitResponse";
import { CreateBookingRequest } from "../../../models/RequestModels/CreateBookingRequest";
import { UpdateBookingRequest } from "../../../models/RequestModels/UpdateBookingRequest";
import { canManageOperations } from "../../../utils/helperFunctions/roleHelper";

type CalendarMode = "multi" | "single";

const toApiDate = (value: string) => {
  if (!value) {
    return "";
  }

  return value.split("T")[0];
};

const addDays = (apiDate: string, days: number) => {
  const date = new Date(`${apiDate}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split("T")[0];
};

const getToday = () => new Date().toISOString().split("T")[0];

const getDateRange = (from: string, until: string) => {
  const dates: string[] = [];
  const current = new Date(`${from}T00:00:00`);
  const end = new Date(`${until}T00:00:00`);

  while (current <= end) {
    dates.push(current.toISOString().split("T")[0]);
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

const getMonthStart = (date: Date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-01`;
};

const getLastDayOfSecondMonth = (apiDate: string) => {
  const [year, month] = apiDate.split("-").map(Number);
  const date = new Date(year, month + 1, 0);
  return date.toISOString().split("T")[0];
};

export const useCalendarScreen = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const today = useMemo(() => getToday(), []);
  const todayDate = useMemo(() => new Date(), []);

  const [calendarMode, setCalendarMode] = useState<CalendarMode>("multi");

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(addDays(today, 14));
  const [appliedStartDate, setAppliedStartDate] = useState(today);
  const [appliedEndDate, setAppliedEndDate] = useState(addDays(today, 14));

  const [singleMonthDate, setSingleMonthDate] = useState(
    getMonthStart(todayDate),
  );

  const [openPropertyIds, setOpenPropertyIds] = useState<number[]>([]);

  const [selectedUnit, setSelectedUnit] = useState<UnitResponse | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedEndDate, setSelectedEndDate] = useState("");
  const [pendingBookingSelection, setPendingBookingSelection] = useState<{
    unit: UnitResponse;
    date: string;
  } | null>(null);

  const [selectedBooking, setSelectedBooking] =
    useState<BookingResponse | null>(null);
  const [editingBooking, setEditingBooking] = useState<BookingResponse | null>(
    null,
  );

  const [bookingFormOpen, setBookingFormOpen] = useState(false);
  const [bookingDetailsOpen, setBookingDetailsOpen] = useState(false);

  const [selectedSingleUnitId, setSelectedSingleUnitId] = useState<
    number | null
  >(null);

  const { properties, loading: propertiesLoading } = useAppSelector(
    (state) => state.properties,
  );
  const { units, loading: unitsLoading } = useAppSelector(
    (state) => state.units,
  );
  const { bookings, loading: bookingsLoading } = useAppSelector(
    (state) => state.bookings,
  );
  const { account, user } = useAppSelector((state) => state.auth);
  const canManage = canManageOperations(user?.role);

  const activeUnits = useMemo(() => {
    return units.filter((unit) => unit.active);
  }, [units]);

  const apiStartDate =
    calendarMode === "single" ? singleMonthDate : appliedStartDate;

  const apiEndDate =
    calendarMode === "single"
      ? getLastDayOfSecondMonth(singleMonthDate)
      : appliedEndDate;

  const dates = useMemo(() => {
    return getDateRange(apiStartDate, apiEndDate);
  }, [apiStartDate, apiEndDate]);

  const singleCalendarDates = useMemo(() => {
    return getDateRange(
      singleMonthDate,
      getLastDayOfSecondMonth(singleMonthDate),
    );
  }, [singleMonthDate]);

  const selectedSingleUnit = useMemo(() => {
    return (
      activeUnits.find((unit) => unit.id === selectedSingleUnitId) ||
      activeUnits[0] ||
      null
    );
  }, [activeUnits, selectedSingleUnitId]);

  useEffect(() => {
    dispatch(fetchProperties());
    dispatch(fetchUnits());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchBookings({ from: apiStartDate, until: apiEndDate }));
  }, [dispatch, apiStartDate, apiEndDate]);

  useEffect(() => {
    if (!selectedSingleUnitId && activeUnits.length > 0) {
      setSelectedSingleUnitId(activeUnits[0].id);
    }
  }, [activeUnits, selectedSingleUnitId]);

  useEffect(() => {
    if (properties.length > 0 && openPropertyIds.length === 0) {
      setOpenPropertyIds(properties.map((property) => property.id));
    }
  }, [properties, openPropertyIds.length]);

  const applyFilters = () => {
    if (!startDate || !endDate || startDate > endDate) {
      return;
    }

    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
  };

  const applyDateRange = (nextStart: string, nextEnd: string) => {
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setAppliedStartDate(nextStart);
    setAppliedEndDate(nextEnd);
  };

  const setDateRangeFromToday = (days: number) => {
    const nextStart = getToday();
    const nextEnd = addDays(nextStart, days);

    applyDateRange(nextStart, nextEnd);
  };

  const goToToday = () => {
    setDateRangeFromToday(14);
  };

  const setFullYear = () => {
    const nextStart = getToday();
    const date = new Date(`${nextStart}T00:00:00`);
    const nextEnd = `${date.getFullYear() + 1}-01-01`;

    applyDateRange(nextStart, nextEnd);
  };

  const previousRange = () => {
    const days = getDaysBetween(appliedStartDate, appliedEndDate);

    applyDateRange(
      addDays(appliedStartDate, -days),
      addDays(appliedEndDate, -days),
    );
  };

  const nextRange = () => {
    const days = getDaysBetween(appliedStartDate, appliedEndDate);

    applyDateRange(
      addDays(appliedStartDate, days),
      addDays(appliedEndDate, days),
    );
  };

  const setThisWeek = () => setDateRangeFromToday(7);
  const setNext7Days = () => setDateRangeFromToday(7);
  const setNext14Days = () => setDateRangeFromToday(14);
  const setThisMonth = () => setDateRangeFromToday(30);

  const refreshBookings = () => {
    dispatch(fetchBookings({ from: apiStartDate, until: apiEndDate }));
  };

  const toggleProperty = (id: number) => {
    setOpenPropertyIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
  };

  const getUnitsForProperty = (propertyId: number) => {
    return activeUnits.filter((unit) => unit.property_id === propertyId);
  };

  const getBookingsForUnit = (unitId: number) => {
    return bookings.filter(
      (booking) =>
        booking.unit_id === unitId &&
        booking.status !== "cancelled" &&
        toApiDate(booking.check_in) <= apiEndDate &&
        toApiDate(booking.check_out) >= apiStartDate,
    );
  };

  const getBookingsForUnitAndDate = (unitId: number, date: string) => {
    return bookings.filter(
      (booking) =>
        booking.unit_id === unitId &&
        booking.status !== "cancelled" &&
        date >= toApiDate(booking.check_in) &&
        date < toApiDate(booking.check_out),
    );
  };

  const singleUnitBookings = useMemo(() => {
    if (!selectedSingleUnit) {
      return [];
    }

    return bookings.filter(
      (booking) =>
        booking.unit_id === selectedSingleUnit.id &&
        booking.status !== "cancelled" &&
        toApiDate(booking.check_in) <= apiEndDate &&
        toApiDate(booking.check_out) >= apiStartDate,
    );
  }, [selectedSingleUnit, bookings, apiStartDate, apiEndDate]);

  const handleSingleCalendarDatePress = (date: string) => {
    if (!canManage) {
      const dayBookings = selectedSingleUnit
        ? getBookingsForUnitAndDate(selectedSingleUnit.id, date)
        : [];

      if (dayBookings.length > 0) {
        openBookingDetails(dayBookings[0]);
      }

      return;
    }

    if (!selectedSingleUnit) {
      return;
    }

    const dayBookings = getBookingsForUnitAndDate(selectedSingleUnit.id, date);

    if (dayBookings.length > 0) {
      openBookingDetails(dayBookings[0]);
      return;
    }

    if (!selectedStartDate) {
      setSelectedUnit(selectedSingleUnit);
      setSelectedStartDate(date);
      return;
    }

    const checkIn = selectedStartDate <= date ? selectedStartDate : date;
    const checkOut = selectedStartDate <= date ? date : selectedStartDate;

    openCreateBookingModal(
      selectedSingleUnit,
      checkIn,
      checkIn === checkOut ? addDays(checkIn, 1) : checkOut,
    );
  };

  const openCreateBookingModal = (
    unit: UnitResponse,
    checkIn?: string,
    checkOut?: string,
  ) => {
    const nextCheckIn = checkIn || apiStartDate;
    const nextCheckOut = checkOut || addDays(nextCheckIn, 1);

    setSelectedUnit(unit);
    setSelectedStartDate(nextCheckIn);
    setSelectedEndDate(nextCheckOut);
    setSelectedBooking(null);
    setEditingBooking(null);
    setBookingDetailsOpen(false);
    setBookingFormOpen(true);
  };

  const handleCalendarDateClick = (unit: UnitResponse, date: string) => {
    if (!canManage) {
      return;
    }

    if (
      !pendingBookingSelection ||
      pendingBookingSelection.unit.id !== unit.id
    ) {
      setPendingBookingSelection({ unit, date });
      return;
    }

    const firstDate = pendingBookingSelection.date;
    const secondDate = date;

    const checkIn = firstDate <= secondDate ? firstDate : secondDate;
    const checkOut = firstDate <= secondDate ? secondDate : firstDate;

    openCreateBookingModal(
      unit,
      checkIn,
      checkIn === checkOut ? addDays(checkIn, 1) : checkOut,
    );

    setPendingBookingSelection(null);
  };

  const openBookingDetails = (booking: BookingResponse) => {
    setSelectedBooking(booking);
    setBookingDetailsOpen(true);
  };

  const closeBookingDetails = () => {
    setBookingDetailsOpen(false);
  };

  const openEditBooking = () => {
    if (!canManage) {
      return;
    }
    setEditingBooking(selectedBooking);
    setBookingDetailsOpen(false);
    setBookingFormOpen(true);
  };

  const closeBookingForm = () => {
    setBookingFormOpen(false);
    setEditingBooking(null);
    setSelectedUnit(null);
    setSelectedStartDate("");
    setSelectedEndDate("");
    setPendingBookingSelection(null);
  };

  const submitBooking = async (
    body: CreateBookingRequest | UpdateBookingRequest,
  ) => {
    const result = editingBooking
      ? await dispatch(
          updateBooking({
            id: editingBooking.id,
            body: body as UpdateBookingRequest,
          }),
        )
      : await dispatch(createBooking(body as CreateBookingRequest));

    if (createBooking.fulfilled.match(result)) {
      setBookingFormOpen(false);
      setEditingBooking(null);
      setSelectedUnit(null);
      setSelectedStartDate("");
      setSelectedEndDate("");
      setPendingBookingSelection(null);
      setSelectedBooking(result.payload);
      setBookingDetailsOpen(true);
      refreshBookings();
      return;
    }

    if (updateBooking.fulfilled.match(result)) {
      setBookingFormOpen(false);
      setEditingBooking(null);
      setSelectedBooking(result.payload);
      setBookingDetailsOpen(true);
      refreshBookings();
    }
  };

  const quickUpdatePaymentStatus = async (
    paymentStatus: BookingResponse["payment_status"],
  ) => {
    if (
      !selectedBooking ||
      selectedBooking.payment_status === paymentStatus ||
      !canManage
    ) {
      return;
    }

    const result = await dispatch(
      updateBooking({
        id: selectedBooking.id,
        body: { payment_status: paymentStatus },
      }),
    );

    if (updateBooking.fulfilled.match(result)) {
      setSelectedBooking(result.payload);
      refreshBookings();
    }
  };

  const confirmCancelBooking = async () => {
    if (
      !selectedBooking ||
      !canManage ||
      !window.confirm("Cancel this booking?")
    ) {
      return;
    }

    const result = await dispatch(cancelBooking(selectedBooking.id));

    if (cancelBooking.fulfilled.match(result)) {
      setBookingDetailsOpen(false);
      setSelectedBooking(null);
      refreshBookings();
    }
  };

  const confirmSendInvoice = async () => {
    if (!selectedBooking || !canManage) {
      return;
    }

    const result = await dispatch(sendBookingInvoice(selectedBooking.id));

    if (sendBookingInvoice.fulfilled.match(result)) {
      window.alert(t("booking.invoiceSent"));
      return;
    }

    window.alert(t("booking.failedSendInvoice"));
  };

  const previousSingleUnit = () => {
    if (!selectedSingleUnit || activeUnits.length === 0) {
      return;
    }

    const currentIndex = activeUnits.findIndex(
      (unit) => unit.id === selectedSingleUnit.id,
    );
    const previousIndex =
      currentIndex <= 0 ? activeUnits.length - 1 : currentIndex - 1;

    setSelectedSingleUnitId(activeUnits[previousIndex].id);
  };

  const nextSingleUnit = () => {
    if (!selectedSingleUnit || activeUnits.length === 0) {
      return;
    }

    const currentIndex = activeUnits.findIndex(
      (unit) => unit.id === selectedSingleUnit.id,
    );
    const nextIndex =
      currentIndex >= activeUnits.length - 1 ? 0 : currentIndex + 1;

    setSelectedSingleUnitId(activeUnits[nextIndex].id);
  };

  const previousMonth = () => {
    const [year, month] = singleMonthDate.split("-").map(Number);
    const date = new Date(year, month - 2, 1);
    setSingleMonthDate(getMonthStart(date));
  };

  const nextMonth = () => {
    const [year, month] = singleMonthDate.split("-").map(Number);
    const date = new Date(year, month, 1);
    setSingleMonthDate(getMonthStart(date));
  };

  const getDaysBetween = (from: string, until: string) => {
    const start = new Date(`${from}T00:00:00`);
    const end = new Date(`${until}T00:00:00`);

    return Math.max(
      1,
      Math.round((end.getTime() - start.getTime()) / 86400000),
    );
  };

  return {
    t,

    calendarMode,
    setCalendarMode,

    startDate,
    endDate,
    setStartDate,
    setEndDate,
    applyFilters,
    setDateRangeFromToday,
    setThisWeek,
    setThisMonth,
    setNext7Days,
    setNext14Days,

    apiStartDate,
    apiEndDate,

    singleMonthDate,
    setSingleMonthDate,
    singleCalendarDates,
    selectedSingleUnit,
    singleUnitBookings,
    handleSingleCalendarDatePress,
    previousSingleUnit,
    nextSingleUnit,
    previousMonth,
    nextMonth,

    properties,
    units: activeUnits,
    bookings,
    dates,
    openPropertyIds,
    toggleProperty,
    getUnitsForProperty,
    getBookingsForUnit,
    getBookingsForUnitAndDate,

    pendingBookingSelection,
    handleCalendarDateClick,
    openCreateBookingModal,
    openBookingDetails,

    selectedUnit,
    selectedStartDate,
    selectedEndDate,
    selectedBooking,
    editingBooking,

    bookingFormOpen,
    bookingDetailsOpen,
    closeBookingForm,
    closeBookingDetails,
    openEditBooking,
    submitBooking,

    quickUpdatePaymentStatus,
    confirmCancelBooking,
    confirmSendInvoice,

    defaultCurrency: account?.default_currency || "BAM",
    loading: propertiesLoading || unitsLoading || bookingsLoading,

    canManage,

    goToToday,
    setFullYear,
    previousRange,
    nextRange,
    refreshBookings,
  };
};
